import React, { useState, useRef, useEffect } from "react";
import {
  Col,
  Card,
  CardBody,
  Row,
  Input,
  Modal,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  FormGroup,
  Label,
} from "reactstrap";
import { cof, checkCof } from "../../../api/cof";
import Webcam from "react-webcam";
import Cropper from "react-easy-crop";
import { FaCamera, FaTimes } from "react-icons/fa";
import Swal from "sweetalert2";
import "./cof.css";
import PropTypes from "prop-types";

const toggleItems = [
  { id: 0, label: "Mechanical readiness of the Vessel" },
  {
    id: 1,
    label: "Equipment availability within the vessel to ensure 'Safety at Sea'",
  },
  {
    id: 2,
    label:
      "Ensure dedicated safety equipment is always accessible, operational, and correctly maintained/serviced by vessel owners",
  },
  {
    id: 3,
    label:
      "Appropriate medical equipment and medicine exists on board for number of crew and length of trip",
  },
  {
    id: 4,
    label:
      "Equipment readiness within the vessel to carry out fishing activities",
  },
  { id: 5, label: "Crew living conditions and accommodation" },
  {
    id: 6,
    label: "Ability to provide protection in the event of a collision or fire",
  },
  {
    id: 7,
    label:
      "Crew has a place to relax and rest which is ventilated, insulated, and separated from work areas",
  },
  {
    id: 8,
    label:
      "Sanitation and washing areas are maintained in a clean and operational condition",
  },
  { id: 9, label: "Availability of Insurance certificate for the vessel." },
  {
    id: 10,
    label:
      "Medical certification requirements to ensure every crew member is fit to perform their duties",
  },
  {
    id: 11,
    label:
      "Essential crew safety training that had been done. The boat needs to have accurate and up-to-date crew and training records.",
  },
  {
    id: 12,
    label:
      "Accident and incident recording requirements and an accident prevention plan.",
  },
  { id: 13, label: "Contracts of the Crew members with the boat owner." },
];

const RightSideContent = ({ sendFunction, requestId, vesselNumber }) => {
  const webcamRef = useRef(null);
  const [status, setStatus] = useState("Accepted");
  const [remarks, setRemarks] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCameraModalOpen, setIsCameraModalOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [currentToggleId, setCurrentToggleId] = useState(null);
  const [uploadedImages, setUploadedImages] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    vesselNumber: vesselNumber,
    requestId,
    tripId: requestId,
    status: "Pending",
    toggles: toggleItems.reduce((acc, item) => {
      acc[item.id] = false;
      return acc;
    }, {}),
    images: {},
  });

  useEffect(() => {
    const fetchCofData = async () => {
      try {
        const response = await checkCof(requestId);
        console.log("COF Data:", response);
        setFormData((prev) => ({
          ...prev,
          status: response.data[0].status || prev.status,
          vesselNumber: response.data[0].vesselNumber || prev.vesselNumber,
          toggles: response.data[0].checklist || prev.toggles,
          images: response.data[0].images || prev.images,
        }));
        setUploadedImages(response.data[0].images || {});
      } catch (error) {
        console.log("Failed to fetch COF data.");
      }
    };
    fetchCofData();
  }, [requestId]);

  const handleChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      toggles: { ...prev.toggles, [name]: checked },
    }));
  };

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const toggleCameraModal = (toggleId) => {
    if (!toggleItems.find((item) => item.id === toggleId)) return;
    setCurrentToggleId(toggleId);
    setIsCameraModalOpen(!isCameraModalOpen);
    setCapturedImage(null);
  };

  const capture = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage(imageSrc);
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = new Image();
    image.src = imageSrc;
    await image.decode();
    const canvas = document.createElement("canvas");
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );
    return canvas.toDataURL("image/jpeg");
  };

  const handleImageCapture = async () => {
    if (!capturedImage || !croppedAreaPixels) return;
    const croppedImage = await getCroppedImg(capturedImage, croppedAreaPixels);
    setUploadedImages((prev) => ({
      ...prev,
      [currentToggleId]: croppedImage,
    }));
    setFormData((prev) => ({
      ...prev,
      images: { ...prev.images, [currentToggleId]: croppedImage },
    }));
    setIsCameraModalOpen(false);
    setCapturedImage(null);
  };

  const handleImageClick = (imageSrc) => setPreviewImage(imageSrc);
  const closePreview = () => setPreviewImage(null);

  const handleSubmit = async (newStatus) => {
    setLoading(true);
    setStatus(newStatus);
    const checklistArray = toggleItems.map((item) => formData.toggles[item.id]);
    if (checklistArray.every((value) => !value)) {
      Swal.fire({
        title: "Error",
        text: "Please select at least one checklist item.",
        icon: "error",
      });
      setLoading(false);
      return;
    }
    const payload = {
      vesselNumber: formData.vesselNumber,
      requestId: formData.requestId,
      tripId: formData.requestId,
      status: newStatus,
      remarks,
      checklist: checklistArray,
      images: formData.images,
    };

    try {
      const prevStatus = formData.status;
      await cof(payload);

      // update local formData status so UI reflects change immediately
      setFormData((prev) => ({ ...prev, status: newStatus }));

      await Swal.fire({
        title: newStatus === "Accepted" ? "Accepted!" : "Rejected!",
        text: `COF ${newStatus} successfully!`,
        icon: newStatus === "Accepted" ? "success" : "warning",
        confirmButtonText: "OK",
      });

      toggleModal();
      sendFunction(formData.vesselNumber);
    } catch (error) {
      // revert local status on error
      setFormData((prev) => ({ ...prev, status: formData.status }));
      Swal.fire({
        title: "Error",
        text: "Failed to submit COF. Please try again.",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Col lg={8}>
        <Card className="candidate-details mt-lg-0">
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: '10px',
              padding: '20px',
              backgroundColor: '#f4f4f4',
            }}
          >
            {formData.status === 'Accepted' ? (
              <h6 className="status-button-success">COF {formData.status}</h6>
            ) : formData.status === 'Rejected' ? (
              <h6 className="status-button-danger">COF {formData.status}</h6>
            ) : (
              <h6 className="status-button-warning">COF Pending</h6>
            )}
          </div>
          <CardBody className="candidate-personal-detail mt-3">
            <Row>
              {toggleItems.map((item) => (
                <Row
                  key={item.id}
                  className="align-items-center"
                  style={{
                    background: '#f4f4f4',
                    margin: '6px 2px',
                    borderRadius: '20px',
                  }}
                >
                  <Col
                    lg={8}
                    style={{ padding: '10px' }}
                  >
                    <div className="toggle-card d-flex align-items-center">
                      <div className="number-badge">{item.id + 1}</div>
                      <span className="text flex-grow-1">{item.label}</span>
                    </div>
                  </Col>
                  <Col
                    lg={4}
                    className="d-flex align-items-center justify-content-end"
                    style={{ padding: '10px' }}
                  >
                    <FaCamera
                      style={{ cursor: 'pointer', marginRight: '15px' }}
                      onClick={() => toggleCameraModal(item.id)}
                    />
                    {uploadedImages[item.id] && (
                      <img
                        src={uploadedImages[item.id]}
                        alt={`Uploaded for ${item.label}`}
                        style={{
                          width: '30px',
                          height: '30px',
                          objectFit: 'cover',
                          borderRadius: '5px',
                          marginRight: '10px',
                          cursor: 'pointer',
                        }}
                        onClick={() =>
                          handleImageClick(uploadedImages[item.id])
                        }
                      />
                    )}
                    <FormGroup
                      switch
                      className="ms-auto custom-switch"
                    >
                      <Input
                        type="switch"
                        id={`switch-${item.id}`}
                        name={item.id}
                        checked={formData.toggles[item.id] || false}
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              ))}
            </Row>
            {previewImage && (
              <div
                className="preview-overlay"
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 1200,
                }}
                onClick={closePreview}
              >
                <div
                  className="preview-container"
                  style={{
                    color: 'white',
                    padding: '20px',
                    borderRadius: '10px',
                    position: 'relative',
                    maxWidth: '100%',
                    maxHeight: '100%',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <FaTimes
                    style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      cursor: 'pointer',
                      fontSize: '20px',
                    }}
                    onClick={closePreview}
                  />
                  <img
                    src={previewImage}
                    alt="Preview"
                    style={{
                      width: '700px',
                      height: '700px',
                      objectFit: 'contain',
                      borderRadius: '4px',
                    }}
                  />
                </div>
              </div>
            )}
            {formData.status !== 'Accepted' && formData.status !== 'Rejected' && (
              <div
                className="d-flex justify-content-end"
                style={{ gap: '10px' }}
              >
                <Button
                  onClick={() => {
                    setStatus('Accepted');
                    setIsModalOpen(true);
                  }}
                  color="secondary"
                  className="btn-black"
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Accept'}
                </Button>
                <Button
                  onClick={() => {
                    setStatus('Rejected');
                    setIsModalOpen(true);
                  }}
                  color="danger"
                  className="btn-red"
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Reject'}
                </Button>
              </div>
            )}

            <Modal
              isOpen={isModalOpen}
              toggle={toggleModal}
            >
              <ModalHeader toggle={toggleModal}>
                {status === 'Accepted' ? 'Form Accepted' : 'Form Rejected'}
              </ModalHeader>
              <ModalBody>
                {status === 'Accepted'
                  ? 'You have accepted the COF form. Please confirm your decision.'
                  : 'You have rejected the COF form. Please confirm your decision.'}
                <div className="mt-3">
                  <Label for="remarks">Remarks</Label>
                  <Input
                    type="textarea"
                    id="remarks"
                    name="remarks"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Enter remarks here"
                    rows="3"
                  />
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="primary"
                  onClick={() => handleSubmit(status)}
                  disabled={loading}
                >
                  Confirm
                </Button>
                <Button
                  color="secondary"
                  onClick={toggleModal}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </ModalFooter>
            </Modal>

            <Modal
              isOpen={isCameraModalOpen}
              toggle={() => toggleCameraModal(null)}
              size="lg"
            >
              <ModalHeader toggle={() => toggleCameraModal(null)}>
                Capture and Crop Photo
              </ModalHeader>
              <ModalBody>
                {!capturedImage ? (
                  <div>
                    <Webcam
                      audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      width="100%"
                      videoConstraints={{ facingMode: 'environment' }}
                    />
                    <Button
                      color="primary"
                      onClick={capture}
                      style={{ marginTop: '10px' }}
                    >
                      Capture Photo
                    </Button>
                  </div>
                ) : (
                  <div style={{ position: 'relative', height: '400px' }}>
                    <Cropper
                      image={capturedImage}
                      crop={crop}
                      zoom={zoom}
                      aspect={4 / 3}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                {capturedImage && (
                  <>
                    <Button
                      color="primary"
                      onClick={handleImageCapture}
                    >
                      Upload Cropped Image
                    </Button>
                    <Button
                      color="secondary"
                      onClick={() => setCapturedImage(null)}
                    >
                      Retake
                    </Button>
                  </>
                )}
                <Button
                  color="secondary"
                  onClick={() => toggleCameraModal(null)}
                >
                  Cancel
                </Button>
              </ModalFooter>
            </Modal>
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

RightSideContent.propTypes = {
  sendFunction: PropTypes.func.isRequired,
  requestId: PropTypes.string.isRequired,
  vesselNumber: PropTypes.string.isRequired,
};

export default RightSideContent;
